<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import DeleteModal from 'dashboard/components/widgets/modal/DeleteModal.vue';
import PlatformBadge from '../../socialMonitoring/components/PlatformBadge.vue';
import ProfileForm from './ProfileForm.vue';
import { roleLabel } from '../../socialMonitoring/utils/politicalRoles.js';

const store = useStore();

const profiles = computed(() => store.getters['socialMonitoring/getProfiles']);
const settings = computed(() => store.getters['socialMonitoring/getSettings']);
const uiFlags = computed(() => store.getters['socialMonitoring/getUiFlags']);

const modalOpen = ref(false);
const editing = ref(null);
const saving = ref(false);
const intervalHours = ref(168);
const collecting = ref(false);
const deleteOpen = ref(false);
const deleting = ref(null);
const deletingInFlight = ref(false);

const INTERVAL_LABELS = {
  6: '6h',
  12: '12h',
  24: 'Diário',
  72: '3 dias',
  168: 'Semanal',
  336: 'Quinzenal',
  720: 'Mensal',
};

const formatDate = d => (d ? new Date(d).toLocaleString('pt-BR') : 'Nunca');

async function bootstrap() {
  await Promise.all([
    store.dispatch('socialMonitoring/fetchProfiles'),
    store.dispatch('socialMonitoring/fetchSettings'),
  ]);
  intervalHours.value = settings.value.collectIntervalHours || 168;
}

function openAdd() {
  editing.value = null;
  modalOpen.value = true;
}

function openEdit(profile) {
  editing.value = profile;
  modalOpen.value = true;
}

async function saveProfile(payload) {
  saving.value = true;
  try {
    if (payload.id) {
      await store.dispatch('socialMonitoring/updateProfile', payload);
      useAlert('Perfil atualizado');
    } else {
      await store.dispatch('socialMonitoring/createProfile', payload);
      useAlert('Perfil criado');
    }
    modalOpen.value = false;
  } catch (e) {
    useAlert(
      e?.response?.data?.errors?.join(', ') || e.message || 'Erro ao salvar'
    );
  } finally {
    saving.value = false;
  }
}

async function toggle(profile) {
  await store.dispatch('socialMonitoring/toggleProfile', profile.id);
}

function askRemove(profile) {
  deleting.value = profile;
  deleteOpen.value = true;
}

function cancelRemove() {
  deleteOpen.value = false;
  deleting.value = null;
}

async function confirmRemove() {
  if (!deleting.value || deletingInFlight.value) return;
  deletingInFlight.value = true;
  try {
    await store.dispatch('socialMonitoring/deleteProfile', deleting.value.id);
    useAlert(`"${deleting.value.name}" removido — dados serão apagados em segundo plano.`);
    deleteOpen.value = false;
    deleting.value = null;
  } catch (e) {
    useAlert(e?.response?.data?.error || e.message || 'Erro ao remover');
  } finally {
    deletingInFlight.value = false;
  }
}

async function collect(profile) {
  await store.dispatch('socialMonitoring/collectProfile', profile.id);
  useAlert(`Coleta de "${profile.name}" enfileirada`);
}

async function collectAll() {
  collecting.value = true;
  try {
    await store.dispatch('socialMonitoring/collectAll');
    useAlert('Coleta de todos os perfis enfileirada');
  } finally {
    collecting.value = false;
  }
}

async function saveInterval() {
  await store.dispatch('socialMonitoring/updateSettings', {
    collectIntervalHours: Number(intervalHours.value),
  });
  useAlert('Intervalo atualizado');
}

onMounted(bootstrap);
</script>

<template>
  <div class="flex h-full w-full flex-col overflow-y-auto p-6">
    <div class="mx-auto w-full max-w-5xl">
      <header class="mb-6 flex items-start justify-between">
        <div>
          <h1 class="text-xl font-bold text-n-slate-12">Redes Sociais</h1>
          <p class="mt-1 text-sm text-n-slate-11">
            Gerencie os perfis monitorados e o intervalo de coleta.
          </p>
        </div>
        <div class="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            icon="i-lucide-download-cloud"
            :is-loading="collecting"
            @click="collectAll"
          >
            Coletar todos
          </Button>
          <Button
            variant="solid"
            color="blue"
            size="sm"
            icon="i-lucide-plus"
            @click="openAdd"
          >
            Adicionar perfil
          </Button>
        </div>
      </header>

      <!-- Global settings -->
      <section class="mb-6 rounded-xl border border-n-weak bg-n-background p-4">
        <h2 class="mb-3 text-sm font-semibold text-n-slate-12">
          Intervalo de coleta (global)
        </h2>
        <div class="flex flex-wrap items-center gap-3">
          <select
            v-model="intervalHours"
            class="h-9 rounded-lg border border-n-weak bg-n-background px-2 text-sm"
          >
            <option v-for="h in settings.presets" :key="h" :value="h">
              {{ INTERVAL_LABELS[h] || `${h}h` }}
            </option>
          </select>
          <Button variant="outline" size="sm" @click="saveInterval">
            Salvar
          </Button>
          <span class="text-xs text-n-slate-11"
            >Aplicado aos perfis que não têm intervalo próprio.</span
          >
        </div>
      </section>

      <!-- Profiles list -->
      <section class="rounded-xl border border-n-weak bg-n-background">
        <div
          class="flex items-center justify-between border-b border-n-weak p-4"
        >
          <h2 class="text-sm font-semibold text-n-slate-12">
            Perfis monitorados
          </h2>
          <span
            v-if="uiFlags.isFetchingProfiles"
            class="text-xs text-n-slate-11"
            >Carregando…</span
          >
        </div>

        <div
          v-if="!profiles.length"
          class="p-8 text-center text-sm text-n-slate-11"
        >
          Nenhum perfil cadastrado. Clique em "Adicionar perfil" para começar.
        </div>

        <table v-else class="w-full text-sm">
          <thead>
            <tr
              class="border-b border-n-weak text-left text-[11px] uppercase tracking-wide text-n-slate-11"
            >
              <th class="p-3">Nome</th>
              <th class="p-3">Plataforma</th>
              <th class="p-3">Tipo</th>
              <th class="p-3">Cargo</th>
              <th class="p-3">Menções</th>
              <th class="p-3">Última coleta</th>
              <th class="p-3">Ativo</th>
              <th class="p-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="p in profiles"
              :key="p.id"
              class="border-b border-n-weak last:border-none"
            >
              <td class="p-3 font-medium text-n-slate-12">{{ p.name }}</td>
              <td class="p-3">
                <PlatformBadge :platform="p.platform" size="xs" />
              </td>
              <td class="p-3 text-xs text-n-slate-11">
                {{ p.type === 'target' ? 'Alvo' : 'Competidor' }}
              </td>
              <td class="p-3">
                <span
                  v-if="p.politicalRole"
                  class="inline-flex items-center rounded-md bg-n-slate-3 px-2 py-0.5 text-[10px] font-semibold text-n-slate-12"
                >
                  {{ roleLabel(p.politicalRole) }}
                </span>
                <span v-else class="text-[10px] text-n-slate-11">—</span>
              </td>
              <td class="p-3 text-xs text-n-slate-11">
                {{ p._count?.mentions ?? 0 }}
              </td>
              <td class="p-3 text-xs text-n-slate-11">
                {{ formatDate(p.lastCollectedAt) }}
              </td>
              <td class="p-3">
                <label class="inline-flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    :checked="p.isActive"
                    class="h-4 w-4 accent-blue-600"
                    @change="toggle(p)"
                  />
                </label>
              </td>
              <td class="p-3 text-right">
                <div class="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    icon="i-lucide-download-cloud"
                    title="Coletar agora"
                    @click="collect(p)"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    icon="i-lucide-pencil"
                    title="Editar"
                    @click="openEdit(p)"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    icon="i-lucide-trash-2"
                    color="ruby"
                    title="Remover"
                    @click="askRemove(p)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>

    <ProfileForm
      :open="modalOpen"
      :profile="editing"
      :saving="saving"
      @close="modalOpen = false"
      @save="saveProfile"
    />

    <DeleteModal
      v-model:show="deleteOpen"
      :on-close="cancelRemove"
      :on-confirm="confirmRemove"
      title="Remover perfil monitorado"
      :message="
        deleting
          ? `Tem certeza que deseja remover '${deleting.name}'? Esta ação é permanente e apaga TODOS os dados associados: menções, comentários, alertas, insights, clusters, narrativas, sugestões de conteúdo, respostas e execuções do copiloto.`
          : ''
      "
      confirm-text="Remover tudo"
      reject-text="Cancelar"
    />
  </div>
</template>
